import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { media } from '../../theme/tokens';
import {
  KpiStrip,
  SelectDropdown,
  PeriodToggle,
  PageHeader
} from '../../components';
import { usePrimeursHeader } from 'apiTyped/hooks/primeurs.query';
import { PrimeursLiveTab } from './LiveTab';
import { PrimeursPreparationTab } from './PreparationTab';
import { PrimeursRecapTab } from './RecapTab';

type Tab = 'live' | 'preparation' | 'recap';

const TAB_OPTIONS: { label: string; value: Tab }[] = [
  { label: 'En direct', value: 'live' },
  { label: 'Préparation', value: 'preparation' },
  { label: 'Récapitulatif', value: 'recap' }
];

export const Primeurs: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('live');
  const { isLoading, kpis, feed } = usePrimeursHeader();

  const kpiItems = useMemo(() => {
    if (!kpis || kpis.length === 0) {
      return [
        { label: 'Avg. deviation', value: '—', unavailable: true },
        { label: 'Avg. price', value: '—', unavailable: true },
        { label: 'PMM price', value: '—', unavailable: true },
        { label: 'Wines tracked', value: '—', unavailable: true }
      ];
    }
    const kpi = kpis[0];
    return [
      {
        label: 'Avg. deviation',
        value: kpi.avg_dev != null ? `${Number(kpi.avg_dev).toFixed(1)}%` : '—'
      },
      {
        label: 'Avg. price',
        value:
          kpi.avg_price != null ? `${Number(kpi.avg_price).toFixed(0)}€` : '—'
      },
      {
        label: 'PMM price',
        value:
          kpi.pmm_price != null ? `${Number(kpi.pmm_price).toFixed(0)}€` : '—'
      },
      {
        label: 'Wines tracked',
        value: kpis.length.toString()
      }
    ];
  }, [kpis]);

  return (
    <PageWrap>
      <PageHeader title="Campagne Primeurs">
        <PeriodToggle
          options={TAB_OPTIONS}
          value={activeTab}
          onChange={(v) => setActiveTab(v as Tab)}
        />
      </PageHeader>

      <KpiStrip items={kpiItems} />

      {feed && feed.length > 0 && (
        <FeedStrip>
          {feed.slice(0, 5).map((item: any, i: number) => (
            <FeedItem key={i}>
              <FeedWine>{item.wine_name}</FeedWine>
              <FeedVintage>{item.vintage}</FeedVintage>
              {item.price && <FeedPrice>{item.price}€</FeedPrice>}
            </FeedItem>
          ))}
        </FeedStrip>
      )}

      <TabContent>
        {activeTab === 'live' && <PrimeursLiveTab />}
        {activeTab === 'preparation' && <PrimeursPreparationTab />}
        {activeTab === 'recap' && <PrimeursRecapTab />}
      </TabContent>
    </PageWrap>
  );
};

export default Primeurs;

// --- Styles ---

const PageWrap = styled.div`
  padding: 24px 0;
`;

const TabContent = styled.div`
  margin-top: 8px;
`;

const FeedStrip = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 12px 0;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--line);
`;

const FeedItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: var(--paper-warm);
  border-radius: var(--radius-md);
  white-space: nowrap;
  font-size: 12.5px;
`;

const FeedWine = styled.span`
  font-weight: 600;
  color: var(--ink);
`;

const FeedVintage = styled.span`
  color: var(--ink-muted);
`;

const FeedPrice = styled.span`
  color: var(--terracotta);
  font-weight: 600;
`;
